import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import DataTable from "react-data-table-component";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

// --- IMPORTS ---
import Layout from "../../../../Layout/Layout";
import CustomerConstants from "../../../../common/CustomerConstants";
// eslint-disable-next-line
import Loader from "../../../../Components/Loader";
import { SearchField } from "../../../../Components/formElements/SearchField";
import { TableStatusField } from "../../../../Components/formElements/TableStatusField";
import { formatDateWithAMPM } from "../../../../common/Validations";
import {
  getEquipmentRentFormList,
  setEquipmentRentDocumentNumber,
} from "../../../../redux/feature/customer_app/customerSlice";
import MultiColumnFilter from "../../../../Components/MultiColumnFilter/MultiColumnFilter";
import CancelModal from "./ERFCancelModel";
import ShowStatus from "../../../../Components/Modal/supplier_app/Workflow/ShowStatus";
import CommonItemLevelDatatableModal from "../../../../Components/Modal/supplier_app/CommonItemLevelDatatableModal";
import { useReactToPrint } from "react-to-print";
import ErfPrintout from "./ErfPrintout";
import EarlyClosureFormPrintout from "./EarlyClosureFormPrintout";
import ClosureRefundFormPrintout from "./ClosureRefundFormPrintout";
import DeliveryChallanPrintout from "./DeliveryChallanPrintout";
import ReceiptPrintOut from "./ReceiptPrintOut";
import toast from "react-hot-toast";
import DispatchModal from "./DispatchModal";
import DispatchDetailsModal from "./DispatchDetailsModal";
import AssignToDispatchModel from "./AssignToDispatchModel";

const BOOLEAN_FILTER_KEYS = ["refundAmountIndicator", "creditNoteIndicator"];

const ListErfForDispatch = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // --- 1. CONFIG & STATE ---
  localStorage.setItem("menu", "Equipment Rent");
  localStorage.setItem("submenu", "Under Service Executive List");

  // Select Data from Redux Store
  // We use optional chaining (?.) and default values (|| {}) to prevent crashes if state is loading
  const { data: reduxData, pagination } =
    useSelector((state) => state?.customer?.equipmentRentForm) || {};
  let userData = useSelector((state) => state?.user); // User Details from Store

  const currentUserId = userData?.data?.userId || "M000";

  // Identify if the user is a Dispatch Manager
  const isDispatchManager = useMemo(() => {
    return userData?.data?.roles?.some((r) => r.roleId === "DISPATCH") || false;
  }, [userData]);

  //  Identify if the user is a Service Executive
  const isServiceExecutive = useMemo(() => {
    return (
      userData?.data?.roles?.some((r) => r.roleId === "SERVICE-EXECUTIVE") ||
      false
    );
  }, [userData]);

  const handleAssignModalSuccess = useCallback(async () => {
    if (isServiceExecutive || isDispatchManager) {
      await fetchData();
    }
    // eslint-disable-next-line
  }, [isServiceExecutive, isDispatchManager]);

  // Local State for Table UI
  // eslint-disable-next-line
  const [loading, setLoading] = useState(false);
  const [perPage, setPerPage] = useState(
    CustomerConstants.ROW_REPORT_PER_PAGE[0],
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTxt, setSearchTxt] = useState("");
  const [isCardVisible, setCardVisible] = useState(true);
  const [filterShow, setFilterShow] = useState(false);

  /* Cancel Modal */
  const [getCancelModalStatus, setCancelModalStatus] = useState(false);
  // eslint-disable-next-line
  const [userAllowed, setUserAllowed] = useState(false);

  // eslint-disable-next-line
  const cancelModalShow = () => {
    setCancelModalStatus(true);
  };
  const cancelModalHide = () => {
    setCancelModalStatus(false);
  };

  /* Handle selected rows */
  const [getSelectedRows, setSelectedRows] = useState([]);
  const handleRowSelected = ({ selectedRows }) => {
    const validRows = selectedRows.filter(
      (item) => item?.statusHeader !== "Cancelled",
    );
    setSelectedRows(validRows);
  };

  const [showDispatchModal, setShowDispatchModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  const dispatchDetailedPage = (row) => {
    setSelectedRow(row);
    setShowDispatchModal(true);
  };
  // eslint-disable-next-line
  const assignDetailedPage = (row) => {
    setSelectedRow(row);
    setShowAssignModal(true);
  };
  // --- 1. ROLE IDENTIFICATION ---
  // eslint-disable-next-line
  const isSalesPerson = useMemo(() => {
    return (
      userData?.data?.roles?.some((r) => r.roleId === "SALES-OFFICER") || false
    );
  }, [userData]);

  // eslint-disable-next-line
  const isStorePerson = useMemo(() => {
    return userData?.data?.roles?.some((r) => r.roleId === "STORE") || false;
  }, [userData]);

  const normalizeBooleanFilters = (filters) =>
    filters.map((condition) => {
      const key = Object.keys(condition)[0];
      if (!BOOLEAN_FILTER_KEYS.includes(key)) return condition;
      const regexStr = condition[key]?.$regex || "";
      const rawValue = regexStr.replace(/\.\*/g, "").toLowerCase();
      if (rawValue === "true" || rawValue === "pending") return { [key]: true };
      if (rawValue === "false" || rawValue === "done") return { [key]: false };
      return condition;
    });

  const fetchData = async (page = 1, size = 10, search = "") => {
    setLoading(true);
    try {
      const payload = {
        queryName: "ASNPERF", //---For getting list of erf for service provider assigned  under service executive"
        page,
        per_page: size,
        searchTerm: search,
        timeZone: "Asia/Calcutta",
        rowFilter: {},
      };

      if (isServiceExecutive) {
        payload.rowFilter = {
          serviceProviderCode: currentUserId,
        };
      }

      await dispatch(getEquipmentRentFormList(payload));
    } catch (error) {
      console.error("Error fetching list", error);
      toast.error("Error loading records");
    } finally {
      setLoading(false);
    }
  };

  // --- 3. TRIGGER FETCH ---
  useEffect(() => {
    // Ensure we have userData before fetching to avoid empty role checks
    // if (userData) {
    //   fetchData(currentPage, perPage, searchTxt);
    // }
    if (userData?.data?.roles?.length) {
      fetchData(currentPage, perPage, searchTxt);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, perPage, userData]);

  // --- HANDLERS ---
  const detailedPage = (row) => {
    // Save document number to store/local before navigating
    dispatch(setEquipmentRentDocumentNumber(row.documentNumber));
    navigate(CustomerConstants.CUSTOMER_V5_MR_ERF_DETAIL);
  };

  // eslint-disable-next-line
  const handleCreate = () => {
    navigate(CustomerConstants.CUSTOMER_V5_MR_CREATE_ERF);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePerRowsChange = (newPerPage) => {
    setPerPage(newPerPage);
    setCurrentPage(1); // Reset to page 1 on size change
  };

  const showStatusInfo = (rowData) => {
    setModalShowStatus(true);
    setSendDataModal(rowData);
    setModelTitle("Status Information");
  };

  const hideModalStatus = () => {
    setModalShowStatus(false);
  };

  const [modalShowStatus, setModalShowStatus] = useState(false);
  const [sendDataModal, setSendDataModal] = useState("");
  const [modelTitle, setModelTitle] = useState("");

  //  Filter configuration for Gate Entry
  const filterColumns = [
    {
      id: "erfType",
      name: "ERF Type",
      selectorKey: "erfType",
      dropDown: true, // Shows InWard/OutWard
    },
    {
      id: "privilegeCase",
      name: "Privilege Case",
      selectorKey: "privilegeCase",
      dropDown: true, // Shows InWard/OutWard
    },
    {
      id: "erfForType",
      name: "ERF For",
      selectorKey: "erfForType",
      dropDown: true,
    },
    {
      id: "salePoint",
      name: "Sale Point",
      selectorKey: "salePoint",
      dropDown: true,
    },
    {
      id: "jobDoneStatus",
      name: "Job Status",
      selectorKey: "jobDoneStatus",
      dropDown: true,
    },
    {
      id: "patientName",
      name: "Patient Name",
      selectorKey: "patientName",
    },
    {
      id: "billingAddress",
      name: "Patient Address",
      selectorKey: "billingAddress",
    },

    {
      id: "mobileNr",
      name: "Mobile Number",
      selectorKey: "mobileNr",
    },
    {
      id: "equipmentNr",
      name: "ERF Number",
      selectorKey: "equipmentNr",
    },
  ];

  //  Handle multi-column filter
  const handleMultiColumnFilter = (filterResult) => {
    setLoading(true);
    try {
      const filtersArray = Array.isArray(filterResult)
        ? filterResult
        : filterResult?.$and || [];

      const normalized = normalizeBooleanFilters(filtersArray);

      const obj = {
        queryName: "ASNPERF",
        page: 1,
        per_page: 10,
        timeZone: "Asia/Calcutta",
        rowFilter: { $and: [] },
      };

      if (isServiceExecutive) {
        obj.rowFilter.$and.push({ serviceProviderCode: currentUserId });
      }

      if (normalized.length > 0) {
        obj.rowFilter.$and.push(...normalized);
      }

      if (obj.rowFilter.$and.length === 0) {
        delete obj.rowFilter;
      }
      // Dispatch to Redux (Thunk handles the API call)
      dispatch(getEquipmentRentFormList(obj));
    } catch (error) {
      console.error("Error fetching list", error);
    } finally {
      setLoading(false);
    }
  };

  //  Handle filter cancel
  const handleFilterCancel = () => {
    setFilterShow(false);
  };

  // //create erf button
  // useEffect(() => {
  //   if (userData?.data?.roles) {
  //     const allowedRoles = ["STORE"];

  //     const isAllowed = userData.data.roles.some((r) =>
  //       allowedRoles.includes(r.roleId),
  //     );

  //     setUserAllowed(isAllowed);
  //   }
  // }, [userData]);

  // const [hasDIspatchRole, setHasDIspatchRole] = useState();

  // useEffect(() => {
  //   if (userData?.data?.roles) {
  //     const allowedRoles = ["SERVICE-EXECUTIVE", "DISPATCH"];
  //     const isAllowed = userData?.data?.roles.some((r) =>
  //       allowedRoles.includes(r.roleId),
  //     );
  //     setHasDIspatchRole(isAllowed);
  //   }
  // }, [userData]);

  const userStore = JSON.parse(localStorage.getItem("userData"));
  const hasstoreRole = userStore?.data?.roles?.some(
    (r) => r.roleId === "STORE",
  );
  const shouldHideColumns = hasstoreRole;

  const [dispatchModal, setDispatchModal] = useState({ show: false, data: [] });

  const [getItemLevelData, setItemLevelData] = useState([]);
  const [getItemLevelColumns, setItemLevelColumns] = useState([]);
  const [getModalTitle, setModalTitle] = useState("");
  const [itemLevelModalShow, setItemLevelModalShow] = useState(false);

  //print
  //po print invocie
  const contentRef = useRef(null);
  const [getLoadingAdvice, setLoadingAdvice] = useState([]);
  // useReactToPrint declared at top level
  const handlePrint = useReactToPrint({
    contentRef,
    // documentTitle: `loadingAdvice_${getLoadingAdvice?.documentNumber || ""}`,
  });
  const handlePrintWithData = (row) => {
    setLoadingAdvice(row);

    // Wait for state updates before printing
    setTimeout(() => {
      handlePrint();
    }, 500);
  };

  //print for earlyclosure
  const contentRef1 = useRef(null);
  const [getEarlyClosure, setEarlyClosure] = useState(null);

  const earlyClosureHandlePrint = useReactToPrint({
    contentRef: contentRef1,
  });

  const handleEarlyClosurePrintWithData = (row) => {
    setEarlyClosure(row);
  };

  useEffect(() => {
    if (getEarlyClosure) {
      earlyClosureHandlePrint();
    }
    // eslint-disable-next-line
  }, [getEarlyClosure]);

  //print for closureRefundForm
  const contentRef2 = useRef(null);
  const [getClosureRefundForm, setClosureRefundForm] = useState(null);

  const closureRefundFormHandlePrint = useReactToPrint({
    contentRef: contentRef2,
  });

  const handleClosureRefundFormPrintWithData = (row) => {
    setClosureRefundForm(row);
  };

  useEffect(() => {
    if (getClosureRefundForm) {
      closureRefundFormHandlePrint();
    }
    // eslint-disable-next-line
  }, [getClosureRefundForm]);

  //print for deliveryChallan
  const contentRef3 = useRef(null);
  const [getdeliveryChallan, setdeliveryChallan] = useState(null);

  const deliveryChallanHandlePrint = useReactToPrint({
    contentRef: contentRef3,
  });

  const handledeliveryChallanPrintWithData = (row) => {
    setdeliveryChallan(row);
  };

  useEffect(() => {
    if (getdeliveryChallan) {
      deliveryChallanHandlePrint();
    }
    // eslint-disable-next-line
  }, [getdeliveryChallan]);

  //print for Receipt
  const contentRef4 = useRef(null);
  const [getReceipt, setReceipt] = useState(null);

  const ReceiptHandlePrint = useReactToPrint({
    contentRef: contentRef4,
  });

  // const handleReceiptPrintWithData = (row) => {
  //   setReceipt(row);
  // };
  const handleReceiptPrintWithData = (row) => {
    setReceipt(row);
    setTimeout(() => {
      ReceiptHandlePrint();
    }, 300);
  };

  useEffect(() => {
    if (getReceipt) {
      ReceiptHandlePrint();
    }
    // eslint-disable-next-line
  }, [getReceipt]);
  // --- 4. COLUMNS DEFINITION (Fixed keys to match API) ---

  // const userROle = JSON.parse(localStorage.getItem("userData"));
  // // const hasDIspatchRole = userROle?.data?.roles?.some(
  // //   (r) => r.roleId === "DISPATCH",
  // // );

  const serviceHistoryLineItemColInfo = [
    {
      name: "Version",
      cell: (row) => <span className="fw-bold">{row?.versionLabel || ""}</span>,
      width: "80px",
    },
    {
      name: "Service Provider",
      cell: (row) => (
        <div>
          <p className="fw-bold">{row?.serviceProviderName || "N/A"}</p>
          <small>{row?.serviceProviderCode || ""}</small>
        </div>
      ),
      width: "180px",
    },
    {
      name: "Category",
      cell: (row) => <span>{row?.serviceAssignCategory || "N/A"}</span>,
      width: "150px",
    },
    {
      name: "Assigned Date",
      cell: (row) => (
        <span>
          {row?.assignDateAndTime
            ? new Date(row.assignDateAndTime).toLocaleString()
            : "N/A"}
        </span>
      ),
      width: "180px",
    },
    {
      name: "Assigned By",
      cell: (row) => <span>{row?.assignedBy || "N/A"}</span>,
      width: "130px",
    },
    {
      name: "Collect Money",
      cell: (row) => (
        <span>
          {row?.shouldCollectMoney ? (
            <span className="badge bg-success">Yes</span>
          ) : (
            <span className="badge bg-secondary">No</span>
          )}
        </span>
      ),
      width: "120px",
    },
  ];

  const serviceHistoryShowModal = (itemLevelData) => {
    setItemLevelData(itemLevelData);
    setItemLevelColumns(serviceHistoryLineItemColInfo);
    setModalTitle("Service History");
    setItemLevelModalShow(true);
  };

  const columns = [
    {
      name: "Created Date/Time",
      selector: (row) => row.orderDate, // MATCHED API KEY
      cell: (row) => (
        <div>
          <span className="fw-medium">{formatDateWithAMPM(row.createdAt)}</span>
        </div>
      ),
      sortable: true,
      width: "190px",
    },
    {
      name: "ERF No",
      selector: (row) => row.documentNumber, // MATCHED API KEY
      cell: (row) => (
        <button className="btnInfo" onClick={() => detailedPage(row)}>
          {/* Fallback if documentNumber is missing */}
          {row.documentNumber || row.equipmentRentNr}
        </button>
      ),
      sortable: true,
      width: "180px",
    },
    {
      name: "ERF Status",
      selector: (row) => row.erfStatus,
      cell: (row) => (
        <span>
          <TableStatusField status={row.erfStatus} />
        </span>
      ),
      width: "220px",
    },
    {
      name: "Job Status",
      cell: (row) => {
        const status = row?.jobDoneStatus;
        if (!status) return <span className="text-muted">--</span>;
        const color =
          status === "Done"
            ? "#28a745"
            : status === "Not Done"
              ? "#dc3545"
              : "#ffc107";
        return (
          <span
            className="badge"
            style={{
              backgroundColor: color,
              color: status === "Pending" ? "black" : "white",
              padding: "6px 10px",
              borderRadius: "10px",
            }}
          >
            {status}
          </span>
        );
      },
      width: "140px",
    },
    {
      name: "Job Remarks",
      cell: (row) => (
        <span style={{ fontSize: "12px" }}>{row?.jobDoneRemarks || "--"}</span>
      ),
      width: "180px",
    },

    // ...(hasDIspatchRole
    //   ? [
    //       //  {
    //       //     name: " Assign To Dispatch",

    //       //     cell: (row) => {
    //       //       // These ERF types should NEVER be dispatched
    //       //       const NON_DISPATCHABLE = ["Service", "Nursing Service"];
    //       //       const isNotDispatchable = NON_DISPATCHABLE.includes(row.erfType);
    //       //       const isReady = row.erfStatus === "Ready To Dispatch";

    //       //       // Disabled if: wrong ERF type OR not ready to dispatch
    //       //       const isDisabled = isNotDispatchable || !isReady;

    //       //       return (
    //       //         <button
    //       //           className="btnInfo"
    //       //           onClick={() => assignDetailedPage(row)}
    //       //           disabled={isDisabled}
    //       //           style={{
    //       //             opacity: isDisabled ? 0.5 : 1,
    //       //             cursor: isDisabled ? "not-allowed" : "pointer",
    //       //             backgroundColor: isNotDispatchable ? "#gray" : "",
    //       //           }}
    //       //           title={
    //       //             isNotDispatchable
    //       //               ? `${row.erfType} cannot be dispatched`
    //       //               : !isReady
    //       //                 ? "ERF is not ready for dispatch"
    //       //                 : "Click to dispatch"
    //       //           }
    //       //         >
    //       //           Assign
    //       //         </button>
    //       //       );
    //       //     },

    //       //     sortable: true,
    //       //     width: "180px",
    //       //   },
    //       {
    //         name: "Dispatch Confirm",

    //         cell: (row) => {
    //           // These ERF types should NEVER be dispatched
    //           const NON_DISPATCHABLE = ["Service", "Nursing Service"];
    //           const isNotDispatchable = NON_DISPATCHABLE.includes(row.erfType);
    //           const isReady = row.erfStatus === "Under Service Executive";

    //           // Disabled if: wrong ERF type OR not ready to dispatch
    //           const isDisabled = isNotDispatchable || !isReady;

    //           return (
    //             <button
    //               className="btnInfo"
    //               onClick={() => dispatchDetailedPage(row)}
    //               disabled={isDisabled}
    //               style={{
    //                 opacity: isDisabled ? 0.5 : 1,
    //                 cursor: isDisabled ? "not-allowed" : "pointer",
    //                 backgroundColor: isNotDispatchable ? "#gray" : "",
    //               }}
    //               title={
    //                 isNotDispatchable
    //                   ? `${row.erfType} cannot be dispatched`
    //                   : !isReady
    //                     ? "ERF is not ready for dispatch"
    //                     : "Click to dispatch"
    //               }
    //             >
    //               Dispatch
    //             </button>
    //           );
    //         },

    //         // cell: (row) => {
    //         //   const isNR = row.erfTypeCode === "NR";
    //         //   const isReady = row.erfStatus === "Ready To Dispatch";

    //         //   // disable if NR OR not ready
    //         //   const isDisabled = isNR || !isReady;

    //         //   return (
    //         //     <button
    //         //       className="btnInfo"
    //         //       onClick={() => dispatchDetailedPage(row)}
    //         //       disabled={isDisabled}
    //         //       style={{
    //         //         opacity: isDisabled ? 0.5 : 1,
    //         //         cursor: isDisabled ? "not-allowed" : "pointer",
    //         //       }}
    //         //     >
    //         //       Dispatch
    //         //     </button>
    //         //   );
    //         // },

    //         sortable: true,
    //         width: "180px",
    //       },
    //       {
    //         name: "Dispatch Details",
    //         cell: (row) => {
    //           const hasDispatch =
    //             Array.isArray(row?.dispatchDetails) &&
    //             row.dispatchDetails.length > 0;
    //           return (
    //             <button
    //               className="btnInfo"
    //               disabled={!hasDispatch}
    //               onClick={() => {
    //                 setSelectedRow(row);
    //                 setDispatchModal({
    //                   show: true,
    //                   data: row?.dispatchDetails || [],
    //                 });
    //               }}
    //               style={{
    //                 opacity: hasDispatch ? 1 : 0.4,
    //                 cursor: hasDispatch ? "pointer" : "not-allowed",
    //               }}
    //             >
    //               <i className="bi bi-truck me-1"></i>
    //               {hasDispatch
    //                 ? `View (${row.dispatchDetails.length})`
    //                 : "No Data"}
    //             </button>
    //           );
    //         },
    //         width: "160px",
    //       },
    //       // {
    //       //   name: "Dispatch Details",
    //       //   cell: (row) => {
    //       //     const hasDispatch =
    //       //       Array.isArray(row?.dispatchDetails) &&
    //       //       row.dispatchDetails.length > 0;
    //       //     return (
    //       //       <button
    //       //         className="btnInfo"
    //       //         disabled={!hasDispatch}
    //       //         onClick={() =>
    //       //           setDispatchModal({
    //       //             show: true,
    //       //             data: row?.dispatchDetails || [],
    //       //           })
    //       //         }
    //       //         style={{
    //       //           opacity: hasDispatch ? 1 : 0.4,
    //       //           cursor: hasDispatch ? "pointer" : "not-allowed",
    //       //         }}
    //       //       >
    //       //         <i className="bi bi-truck me-1"></i>
    //       //         {hasDispatch
    //       //           ? `View (${row.dispatchDetails.length})`
    //       //           : "No Data"}
    //       //       </button>
    //       //     );
    //       //   },
    //       //   width: "160px",
    //       // },
    //     ]
    //   : []),

    // --- SHOW 'ASSIGN' BUTTON ONLY TO DISPATCH MANAGERS ---
    ...(isDispatchManager
      ? [
          {
            name: "Assign Service Executive",
            // cell: (row) => {
            //   const NON_DISPATCHABLE = ["Nursing Service"];
            //   const isNotDispatchable = NON_DISPATCHABLE.includes(row.erfType);

            //   const isReady = row.erfStatus === "Ready To Dispatch";
            //   // eslint-disable-next-line
            //   const isDisabled = isNotDispatchable || !isReady;

            //   return (
            //     <button
            //       className="btnInfo"
            //       onClick={() => assignDetailedPage(row)}
            //       // disabled={isDisabled}
            //       // style={{
            //       //   opacity: isDisabled ? 0.5 : 1,
            //       //   cursor: isDisabled ? "not-allowed" : "pointer",
            //       //   backgroundColor: isNotDispatchable ? "gray" : "",
            //       // }}
            //       // title={isDisabled ? "Cannot Assign yet" : "Click to Assign"}
            //     >
            //       Assign
            //     </button>
            //   );
            // },
            cell: (row) => {
              const NON_DISPATCHABLE = ["Nursing Service"];
              const isNotDispatchable = NON_DISPATCHABLE.includes(row.erfType);

              // On this page erfStatus is "Under Service Executive"
              // Enable assign only when latest SE marked job as "Not Done"
              const isNotDone = row?.jobDoneStatus === "Not Done";

              const isDisabled = isNotDispatchable || !isNotDone;

              const title = isNotDispatchable
                ? `${row.erfType} cannot be assigned`
                : row?.jobDoneStatus === "Pending"
                  ? "Waiting for SE to complete or mark as Not Done"
                  : row?.jobDoneStatus === "Done"
                    ? "Job already completed"
                    : isNotDone
                      ? "SE marked as Not Done — click to re-assign"
                      : "No active assignment";

              return (
                <button
                  className="btnInfo"
                  onClick={() => assignDetailedPage(row)}
                  disabled={isDisabled}
                  style={{
                    opacity: isDisabled ? 0.5 : 1,
                    cursor: isDisabled ? "not-allowed" : "pointer",
                  }}
                  title={title}
                >
                  {isNotDone ? "Re-Assign" : "Assign"}
                </button>
              );
            },

            sortable: true,
            width: "220px",
          },
        ]
      : []),

    // --- SHOW 'DISPATCH' BUTTON ONLY TO SERVICE EXECUTIVES ---
    ...(isServiceExecutive || isDispatchManager
      ? [
          {
            name: "Dispatch Confirm",
            cell: (row) => {
              const NON_DISPATCHABLE = ["Nursing Service"];
              const isNotDispatchable = NON_DISPATCHABLE.includes(row.erfType);

              // Only allow them to dispatch if it has been assigned to them
              const isReady =
                row.erfStatus === "Under Service Executive" ||
                row.erfStatus === "Assign Service Provider";
              const isJobNotDone = row?.jobDoneStatus === "Not Done";
              const isDisabled = isNotDispatchable || !isReady || isJobNotDone;

              return (
                <button
                  className="btnInfo"
                  onClick={() => dispatchDetailedPage(row)}
                  disabled={isDisabled}
                  style={{
                    opacity: isDisabled ? 0.5 : 1,
                    cursor: isDisabled ? "not-allowed" : "pointer",
                    backgroundColor: isNotDispatchable ? "gray" : "",
                  }}
                  title={
                    isJobNotDone
                      ? "Job marked as Not Done — waiting for re-assignment"
                      : isDisabled
                        ? "Cannot Dispatch"
                        : "Click to Dispatch"
                  }
                >
                  Dispatch
                </button>
              );
            },
            sortable: true,
            width: "180px",
          },
        ]
      : []),

    {
      name: "ERF Type & Privilege Case ",
      selector: (row) => row.erfType,
      cell: (row) => (
        <div>
          <span>
            <TableStatusField status={row.erfType} />
          </span>
          <p className="fw-bold mt-1">{row.privilegeCase}</p>
        </div>
      ),
      sortable: true,
      style: { justifyContent: "left" },
      width: "210px",
    },

    {
      name: "ERF For",
      selector: (row) => row.erfForType,
      cell: (row) => (
        // <span className="fw-bold text-primary">{row.erfForType}</span>
        <span
          // className="badge"
          style={{
            color: row.erfForType === "Hospital" ? "red" : "green",
            fontWeight: "bold",
          }}
        >
          {row.erfForType}
        </span>
      ),
      sortable: true,
      width: "120px",
    },
    {
      name: "Patient Details",
      selector: (row) => row.patientName, // MATCHED API KEY
      cell: (row) => (
        <div>
          <p className="fw-bold">{row.patientName}</p>
          <p>{row.contactNumber}</p>
          <p>{row.billingAddress}</p>
        </div>
      ),
      sortable: true,
      width: "160px",
    },
    {
      name: "Amount",
      selector: (row) => row?.netAmount || 0,
      cell: (row) => (
        <div
          className="fw fw-bold"
          style={{
            color: "blue",
            fontWeight: "bold",
          }}
        >
          {`${Number(row?.netAmount || 0).toFixed(2)} INR`}
        </div>
      ),
      style: {
        justifyContent: "flex-end",
      },
      width: "130px",
    },
    {
      name: "Sale Point",
      selector: (row) => row.salePoint, // MATCHED API KEY
      cell: (row) => (
        <div>
          <p>{row.salePoint}</p>
          <p className="fw fw-bold">{row.createdBy}</p>
        </div>
      ),
      sortable: true,
      wrap: true,
      width: "120px",
    },

    {
      name: "Updated Remarks",
      cell: (row) => {
        const version = row?.version || "V0";
        const count = row?.updateRemarks?.length || 0;

        return (
          <>
            <span
              style={{ marginLeft: "12px" }}
              onClick={() => remarkItemShowModal(row?.updateRemarks || [])}
            >
              <i className="bi bi-calendar2-week iconTable"></i>
            </span>

            <span className="suppliersIcon">{`${count}/${version}`}</span>
          </>
        );
      },
      width: "160px",
    },
    {
      name: "Service History",
      cell: (row) => {
        const serviceExecutives = row?.assignedServiceExecutives || [];
        const count = serviceExecutives.length;
        return (
          <>
            <span
              style={{
                marginLeft: "12px",
                cursor: count > 0 ? "pointer" : "not-allowed",
                opacity: count > 0 ? 1 : 0.5,
              }}
              onClick={() => {
                if (count > 0) {
                  serviceHistoryShowModal(
                    serviceExecutives.map((item, index) => ({
                      ...item,
                      versionLabel: `V${index + 1}`,
                    })),
                  );
                }
              }}
            >
              <i className="bi bi-calendar2-week iconTable"></i>
            </span>
            <span className="suppliersIcon">{`${count} Assigned`}</span>
          </>
        );
      },
      width: "200px",
    },

    ...(reduxData?.some(
      (row) =>
        Array.isArray(row?.workflowActionLogs) &&
        row.workflowActionLogs.length > 0,
    )
      ? [
          {
            name: "Workflow log",
            selector: (row) => row.workflowActionLogs,
            cell: (row) => {
              const hasLogs =
                Array.isArray(row?.workflowActionLogs) &&
                row.workflowActionLogs.length > 0;

              return (
                <div
                  style={{ width: "100%" }}
                  className="d-flex justify-content-center iconTable"
                >
                  <img
                    src="assets/img/newIcons/workflow.png"
                    alt="workflow"
                    style={{
                      width: "35px",
                      opacity: hasLogs ? 1 : 0.4,
                      cursor: hasLogs ? "pointer" : "not-allowed",
                    }}
                    className="mt-1"
                    onClick={
                      hasLogs
                        ? () => showStatusInfo(row.workflowActionLogs)
                        : undefined
                    }
                  />
                </div>
              );
            },
            sortable: true,
            width: "150px",
          },
        ]
      : []),

    {
      name: "WorkFlow Status",
      selector: (row) => row.status, // MATCHED API KEY
      cell: (row) => (
        <span>
          <TableStatusField status={row.status} />
        </span>
      ),
      width: "160px",
    },
    ...(!shouldHideColumns
      ? [
          {
            name: "Receipt",
            selector: (row) => row.attachments,
            cell: (row) => (
              <div
                onClick={() => handleReceiptPrintWithData(row)}
                className="flex items-center justify-center w-9 h-9 rounded-full bg-blue-100 text-blue-600 cursor-pointer hover:bg-blue-600 hover:text-white hover:scale-110 hover:shadow-lg active:scale-95"
              >
                <i className="bi bi-printer text-lg fs-5"></i>
              </div>
            ),
            sortable: true,
            width: "130px",
          },
          {
            name: "Agremnent",
            selector: (row) => row.attachments,
            cell: (row) => (
              <div
                onClick={() => handlePrintWithData(row)}
                className="flex items-center justify-center w-9 h-9 rounded-full bg-blue-100 text-blue-600 cursor-pointer hover:bg-blue-600 hover:text-white hover:scale-110 hover:shadow-lg active:scale-95"
              >
                <i className="bi bi-printer text-lg fs-5"></i>
              </div>
            ),
            sortable: true,
            width: "130px",
          },
          {
            name: "Early Closure Form",
            selector: (row) => row.attachments,
            cell: (row) => (
              <div
                onClick={() => handleEarlyClosurePrintWithData(row)}
                className="flex items-center justify-center w-9 h-9 rounded-full bg-blue-100 text-blue-600 cursor-pointer hover:bg-blue-600 hover:text-white hover:scale-110 hover:shadow-lg active:scale-95"
              >
                <i className="bi bi-printer text-lg fs-5"></i>
              </div>
            ),
            sortable: true,
            width: "180px",
          },
          {
            name: "Closure Refund Form",
            selector: (row) => row.attachments,
            cell: (row) => (
              <div
                onClick={() => handleClosureRefundFormPrintWithData(row)}
                className="flex items-center justify-center w-9 h-9 rounded-full bg-blue-100 text-blue-600 cursor-pointer hover:bg-blue-600 hover:text-white hover:scale-110 hover:shadow-lg active:scale-95"
              >
                <i className="bi bi-printer text-lg fs-5"></i>
              </div>
            ),
            sortable: true,
            width: "180px",
          },
          {
            name: "Delivery Challan",
            selector: (row) => row.attachments,
            cell: (row) => (
              <div
                onClick={() => handledeliveryChallanPrintWithData(row)}
                className="flex items-center justify-center w-9 h-9 rounded-full bg-blue-100 text-blue-600 cursor-pointer hover:bg-blue-600 hover:text-white hover:scale-110 hover:shadow-lg active:scale-95"
              >
                <i className="bi bi-printer text-lg fs-5"></i>
              </div>
            ),
            sortable: true,
            width: "180px",
          },
        ]
      : []),
  ];

  const remarkLineItemColInfo = [
    {
      name: "Remarks",
      cell: (row) => <span>{row?.remark || ""} </span>,
      width: "180px",
    },
    {
      name: "Version",
      cell: (row) => <span>{row?.version || ""} </span>,
      width: "180px",
    },
  ];

  const remarkItemShowModal = (itemLevelData) => {
    setItemLevelData(itemLevelData);
    setItemLevelColumns(remarkLineItemColInfo);
    setModalTitle("ERF Remarks / Version");
    setItemLevelModalShow(true);
  };
  const itemLevelHideModal = () => {
    setItemLevelModalShow(false);
  };
  return (
    <main className="dashboard main" id="main">
      <Layout />
      <Row className="justify-center subContent">
        <Col xs={12} md={12} className="rightSide">
          <div className="rightSideInner PageCommonCSS">
            {/* --- Header Section --- */}
            <Row>
              <Col xs={12} md={8} lg={8}>
                <h2 className="mainTitle">ERF - Under Service Executive</h2>
                <p className="subText">List of active and closed rentals</p>
              </Col>

              <Col xs={12} md={3} lg={3} className="mt-3">
                <SearchField
                  name="searchKey"
                  value={searchTxt}
                  onChange={(e) => setSearchTxt(e.target.value)}
                  onClick={() => fetchData(1, perPage, searchTxt)}
                  placeholder="Search..."
                />
              </Col>

              <Col xs={4} md={1} lg={1}>
                <span
                  className="filterIcon"
                  onClick={() => setFilterShow(!filterShow)}
                >
                  <i className="bi bi-filter"></i>
                </span>
              </Col>
            </Row>
            <hr />
            <Row>
              <Col xs={12} md={12} lg={12}>
                {filterShow && (
                  <MultiColumnFilter
                    columns={filterColumns}
                    data={reduxData}
                    onFilter={handleMultiColumnFilter}
                    onCancel={handleFilterCancel}
                    output="and"
                  />
                )}
              </Col>
            </Row>

            {/* --- Table Accordion --- */}
            <div className="accordion" id="accordionExample">
              <div className="accordion-item">
                <h2 className="accordion-header">
                  <Row className="accordion-button">
                    <Col xs={1} md={1} lg={1} className="text-left arrowBtn">
                      <i
                        className={`bi ${
                          isCardVisible ? "bi-chevron-down" : "bi-chevron-right"
                        }`}
                        onClick={() => setCardVisible(!isCardVisible)}
                      ></i>
                    </Col>
                    <Col xs={1} md={1} lg={1} className="text-center logoBtn">
                      <span className="iconLogo">RL</span>
                    </Col>
                    <Col xs={6} md={4} lg={4}>
                      <span className="headerTitle">
                        ERF- Under Service Executive
                      </span>
                      <span className="subHeaderTitle">
                        View Details and Status
                      </span>
                    </Col>

                    {/* Create Button in Accordion Header */}
                    {/* <Col xs={12} md={8} lg={8} className="headerButtonAlign">
                      {!isStorePerson && !hasDIspatchRole && (
                        <Col xs={4} md={2} lg={2} className="ms-auto">
                          <button className="btnTable" onClick={handleCreate}>
                            <i className="bi bi-plus-lg mx-2"></i> Create ERF
                          </button>
                        </Col>
                      )}

                      <Col xs={3} md={2} lg={2} className="">
                        <button
                          className="btnTable"
                          onClick={cancelModalShow}
                          disabled={getSelectedRows.length === 1 ? false : true}
                        >
                          <i className="bi bi-x-circle"></i> Cancel
                        </button>
                      </Col>
                    </Col> */}
                  </Row>
                </h2>

                <div
                  className={`accordion-collapse collapse show ${
                    isCardVisible ? "visible" : ""
                  }`}
                >
                  <div className="accordion-body">
                    <Row>
                      <Col xs={12}>
                        {/* <Loader isLoading={loading || status} /> */}
                        <DataTable
                          title=""
                          columns={columns}
                          // Directly pass the data array from Redux
                          data={reduxData || []}
                          pagination
                          paginationServer
                          paginationPerPage={perPage}
                          paginationDefaultPage={currentPage}
                          // Pass total rows from Redux pagination
                          paginationTotalRows={pagination?.total || 0}
                          paginationRowsPerPageOptions={
                            CustomerConstants.ROW_REPORT_PER_PAGE
                          }
                          // Handlers
                          onChangePage={handlePageChange}
                          onChangeRowsPerPage={handlePerRowsChange}
                          selectableRows
                          highlightOnHover
                          pointerOnHover
                          persistTableHead={false}
                          onSelectedRowsChange={handleRowSelected}
                          // noDataComponent={
                          //   <div className="p-4 text-center text-muted">
                          //     No records found
                          //   </div>
                          // }
                        />
                      </Col>
                    </Row>
                    <div style={{ display: "none" }}>
                      <ErfPrintout ref={contentRef} data={getLoadingAdvice} />
                    </div>
                    <div style={{ display: "none" }}>
                      <EarlyClosureFormPrintout
                        ref={contentRef1}
                        data={getEarlyClosure}
                      />
                    </div>
                    <div style={{ display: "none" }}>
                      <ClosureRefundFormPrintout
                        ref={contentRef2}
                        data={getClosureRefundForm}
                      />
                    </div>
                    <div style={{ display: "none" }}>
                      <DeliveryChallanPrintout
                        ref={contentRef3}
                        data={getdeliveryChallan}
                      />
                    </div>
                    <div style={{ display: "none" }}>
                      <ReceiptPrintOut
                        key={
                          getReceipt?.documentNumber || "Receipt-Printout-key"
                        }
                        ref={contentRef4}
                        data={getReceipt}
                        isExtension={false}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Col>
      </Row>
      <CancelModal
        title="Cancel the ERF Form"
        show={getCancelModalStatus}
        onHide={cancelModalHide}
        selectedrow={getSelectedRows}
      />
      <ShowStatus
        show={modalShowStatus}
        onHide={() => hideModalStatus()}
        rowdatamodal={sendDataModal}
        modeltitle={modelTitle}
      />

      <CommonItemLevelDatatableModal
        show={itemLevelModalShow}
        onHide={() => itemLevelHideModal()}
        columns={getItemLevelColumns}
        data={getItemLevelData}
        title={getModalTitle}
      />
      <DispatchModal
        // key={selectedRow?.documentNumber || ""}
        key={`dispatch-${selectedRow?.documentNumber || "empty"}`}
        show={showDispatchModal}
        onHide={() => {
          setShowDispatchModal(false);
          setSelectedRow(null);
        }}
        erfData={selectedRow}
        onSuccess={handleAssignModalSuccess}
      />
      <AssignToDispatchModel
        // key={selectedRow?.documentNumber || ""}
        key={`assign-${selectedRow?.documentNumber || "empty"}`}
        show={showAssignModal}
        onHide={() => setShowAssignModal(false)}
        erfData={selectedRow}
        onSuccess={handleAssignModalSuccess}
      />
      <DispatchDetailsModal
        // key={selectedRow?.documentNumber || ""}
        key={`details-${selectedRow?.documentNumber || "empty"}`}
        show={dispatchModal.show}
        onHide={() => setDispatchModal({ show: false, data: [] })}
        dispatchDetails={dispatchModal.data}
        erfData={selectedRow}
      />
    </main>
  );
};

export default ListErfForDispatch;
